import React from 'react';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import type { MedicalInfoRequestItem } from '../../constants/patient';
import {
  medicalInfoRequestStatusOptions,
  medicalInfoRequestStatusSxColor,
} from '../../constants/patient';
import { isPatientDateUnset } from '../../utils/patientDateFormat';

type PatientDisplayDateFormat = 'iso' | 'shortWithWeekday';

function formatPatientDisplayDate(iso: string, format: PatientDisplayDateFormat): string {
  const d = dayjs(iso.slice(0, 10));
  if (!d.isValid()) return '—';
  if (format === 'iso') return iso.slice(0, 10);
  return d.locale('ja').format('MM/DD (ddd)');
}

const detailTextSx = { fontSize: '0.8125rem', lineHeight: 1.43 };

/** 初診日・同意書アポなど、未登録時は赤字で「未調整」 */
export const PatientUnsetDateValue: React.FC<{
  iso: string | null | undefined;
  dateFormat?: PatientDisplayDateFormat;
  noWrap?: boolean;
  /** 患者情報画面の TextValue に合わせる */
  compact?: boolean;
}> = ({ iso, dateFormat = 'shortWithWeekday', noWrap = true, compact = false }) => {
  const sx = compact ? detailTextSx : undefined;
  if (isPatientDateUnset(iso)) {
    return (
      <Typography variant="body2" color="error" noWrap={noWrap} sx={sx}>
        未調整
      </Typography>
    );
  }
  return (
    <Typography variant="body2" color="text.primary" noWrap={noWrap} sx={sx}>
      {formatPatientDisplayDate(iso!, dateFormat)}
    </Typography>
  );
};

/** 診療情報提供書依頼（依頼先＋ステータス色分け） */
export const PatientMedicalInfoRequestsValue: React.FC<{
  items: MedicalInfoRequestItem[];
  compact?: boolean;
}> = ({ items, compact = false }) => {
  const visible = items.filter((item) => item.destination.trim() !== '');
  const emptySx = compact
    ? { ...detailTextSx, color: 'text.secondary' }
    : { color: 'text.secondary' };

  if (visible.length === 0) {
    return (
      <Typography variant="body2" sx={emptySx}>
        —
      </Typography>
    );
  }

  const lineSx = compact
    ? { ...detailTextSx, wordBreak: 'break-word' }
    : { wordBreak: 'break-word', lineHeight: 1.4 };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {visible.map((item, i) => {
        const statusLabel =
          medicalInfoRequestStatusOptions.find((o) => o.value === item.status)?.label ??
          item.status;
        return (
          <Typography key={i} variant="body2" sx={lineSx}>
            {item.destination}：
            <Box component="span" sx={{ color: medicalInfoRequestStatusSxColor(item.status) }}>
              {statusLabel}
            </Box>
          </Typography>
        );
      })}
    </Box>
  );
};
